import { useRef, useCallback, RefObject } from "react";

/**
 * Swipe-down-to-dismiss for bottom-sheet modals.
 * Attach `handleRef` to the drag handle / header area,
 * and `sheetRef` to the sheet container that should translate.
 *
 * Only tracks vertical swipe on the header — scrollable content below is unaffected.
 */
export function useSwipeToDismiss(
    onDismiss: () => void,
    options?: { threshold?: number }
) {
    const threshold = options?.threshold ?? 80;
    const sheetRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;
    const startY = useRef(0);
    const currentY = useRef(0);
    const dragging = useRef(false);

    const applyTransform = (dy: number) => {
        if (!sheetRef.current) return;
        const clamped = Math.max(0, dy); // only allow downward
        sheetRef.current.style.transform = `translateY(${clamped}px)`;
        sheetRef.current.style.transition = "none";
    };

    const resetTransform = () => {
        if (!sheetRef.current) return;
        sheetRef.current.style.transition = "transform 0.25s ease-out";
        sheetRef.current.style.transform = "translateY(0)";
    };

    const onTouchStart = useCallback((e: React.TouchEvent) => {
        startY.current = e.touches[0].clientY;
        currentY.current = startY.current;
        dragging.current = true;
    }, []);

    const onTouchMove = useCallback((e: React.TouchEvent) => {
        if (!dragging.current) return;
        currentY.current = e.touches[0].clientY;
        const dy = currentY.current - startY.current;
        applyTransform(dy);
    }, []);

    const onTouchEnd = useCallback(() => {
        if (!dragging.current) return;
        dragging.current = false;
        const dy = currentY.current - startY.current;
        if (dy > threshold) {
            if (sheetRef.current) {
                sheetRef.current.style.transition = "transform 0.2s ease-in";
                sheetRef.current.style.transform = "translateY(100%)";
            }
            setTimeout(onDismiss, 200);
        } else {
            resetTransform();
        }
    }, [onDismiss, threshold]);

    const handleProps = {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    };

    return { sheetRef, handleProps };
}
